/**
 * t622u: "Schedule" next to Send. A small clock button opens the quick choices (t656u: This morning at 9:00 and Later
 * today at 17:00 while still ahead, Tomorrow at 9:00, Monday at 9:00; three at most, see scheduleChoices) and Custom…
 * (a UK date + time). Times are Europe/London.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, Clock } from 'lucide-react';
import { londonDateTime, londonInputs, scheduleChoices, sendTimeLabel } from '@tradeoutlet/shared';
import { Field, Input, Menu, MenuItem, Modal } from '@/components/ui';
import { DialogFooter } from '@/components/ui/DialogFooter';

export function ScheduleButton({ phone, disabled, onSchedule }: { phone?: boolean; disabled?: boolean; onSchedule: (sendAt: string) => void }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  // Worked out when the menu opens, so "This morning" and "Later today" disappear once 9:00 and 17:00 have passed.
  const choices = open ? scheduleChoices(new Date()) : [];
  return (
    <>
      <Menu open={open} setOpen={v => setOpen(v && !disabled)} up align="right" trigger={
        <button type="button" title="Schedule" aria-label="Schedule message" aria-haspopup="menu" aria-expanded={open} disabled={disabled} data-testid="composer-schedule"
          className={clsx('flex shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-slate-800', phone ? 'h-11 w-10' : 'h-8 w-8')}>
          <Clock aria-hidden className="h-4 w-4" />
        </button>}>
        <div className="px-3 pb-1 pt-1.5 text-xs text-slate-500">Schedule message</div>
        {choices.map(c => (
          <MenuItem key={c.id} onClick={() => { setOpen(false); onSchedule(c.at); }} data-testid={`schedule-${c.id}`}>{c.label}</MenuItem>
        ))}
        <MenuItem onClick={() => { setOpen(false); setCustom(true); }} icon={<CalendarClock className="h-4 w-4 text-slate-500" />} data-testid="schedule-custom">Custom…</MenuItem>
      </Menu>
      {custom && <ScheduleTimeDialog title="Schedule message" saveLabel="Schedule" onClose={() => setCustom(false)} onPick={at => { setCustom(false); onSchedule(at); }} />}
    </>
  );
}

/** Date + time (UK) for Custom… and for changing a scheduled message's time. */
export function ScheduleTimeDialog({ title, saveLabel, initial, onPick, onClose }: { title: string; saveLabel: string; initial?: string; onPick: (at: string) => void; onClose: () => void }) {
  const start = londonInputs(initial ?? scheduleChoices(new Date()).find(c => c.id === 'tomorrow')!.at);
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [error, setError] = useState<string | null>(null);
  const r = londonDateTime(date, time, new Date());
  const save = () => { const now = londonDateTime(date, time, new Date()); if (!now.ok) { setError(now.error); return; } onPick(now.at); };
  return (
    <Modal open onClose={onClose} title={title} footer={<DialogFooter onCancel={onClose} onSave={save} saveLabel={saveLabel} saveTestId="schedule-time-save" />}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={date} onChange={e => { setDate(e.target.value); setError(null); }} data-testid="schedule-date" /></Field>
        <Field label="Time"><Input type="time" value={time} onChange={e => { setTime(e.target.value); setError(null); }} data-testid="schedule-time" /></Field>
      </div>
      <p className={clsx('mt-2 text-sm', error ? 'text-rose-600' : 'text-slate-500')} aria-live="polite" data-testid="schedule-time-note">
        {error ?? (r.ok ? `Sends ${sendTimeLabel(r.at)} (UK time)` : 'UK time')}
      </p>
    </Modal>
  );
}
